import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircleOutline as CheckIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useCartStore } from '@/stores/cartStore';
import { ordersApi } from '@/api/orders';
import { useAuthStore } from '@/stores/authStore';

/**
 * Computes free qty for a "buy X get Y free" scheme, honoring three
 * priorities in strict order:
 *   1. paid + free must ALWAYS be a whole integer.
 *   2. paid and free must always be multiples of 0.5.
 *   3. never bump the entered qty by 1 or more — only ever by less than 1.
 *
 * Entered qty is always a whole integer, so the only two 0.5-grid points
 * within [qty, qty+1) are `qty` itself and `qty + 0.5`:
 *   Candidate A (no bump): qty stays whole -> free must be whole (floor).
 *   Candidate B (+0.5 bump): qty ends in .5 -> free must also end in .5
 *                            (that's the only way qty+free stays whole).
 * Whichever candidate gives MORE free wins (most faithful to the scheme
 * ratio); a tie goes to Candidate A (no bump), per priority 3.
 */
function computeScheme(qty: number, scheme?: string, maxTotal?: number): { paid: number; free: number } {
  const fallback = { paid: qty, free: 0 };
  if (!scheme || qty <= 0) return fallback;
  const parts = scheme.split(':');
  if (parts.length !== 2) return fallback;
  const buy = parseInt(parts[0], 10);
  const freeN = parseInt(parts[1], 10);
  if (isNaN(buy) || isNaN(freeN) || buy <= 0 || freeN <= 0) return fallback;

  const q = Math.floor(qty);
  const rate = freeN / buy;

  const freeA = Math.floor(rate * q + 1e-9);                            // Candidate A — no bump
  const qtyB = q + 0.5;                                                 // Candidate B — +0.5 bump
  const freeExactB = rate * qtyB;
  const freeB = freeExactB >= 0.5 - 1e-9 ? Math.floor(freeExactB - 0.5 + 1e-9) + 0.5 : null;

  if (freeB !== null && freeB > freeA + 1e-9 && (maxTotal === undefined || qtyB + freeB <= maxTotal + 1e-9)) {
    return { paid: qtyB, free: freeB };
  }
  return { paid: q, free: freeA };
}

function capQtyToAvailable(raw: number, scheme: string | undefined, availableQty: number | undefined | null, stockHidden: boolean | undefined): number {
  if (!availableQty || availableQty <= 0 || stockHidden) return raw;
  const { paid, free } = computeScheme(raw, scheme, availableQty);
  if (paid + free <= availableQty) return raw;
  for (let q = raw - 1; q >= 1; q--) {
    const { paid: p, free: f } = computeScheme(q, scheme, availableQty);
    if (p + f <= availableQty) return q;
  }
  return 1;
}

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface PlacedEntry {
  distId: number;
  name: string;
  orderNumber: string;
}

function CartQtyField({ storeQty, onCommit, inputSx }: {
  storeQty: number; onCommit: (qty: number) => void; inputSx?: object;
}) {
  const display = storeQty % 1 === 0 ? Math.floor(storeQty) : storeQty;
  const [text, setText] = useState(String(display));
  const [rev, setRev] = useState(0);
  useEffect(() => { setText(String(display)); }, [display, rev]);
  const commit = () => {
    onCommit(Number(text));
    setRev(r => r + 1);
  };
  return (
    <TextField
      size="small"
      type="number"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      inputProps={{ min: 1, 'aria-label': 'Quantity', style: { textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#152346', padding: '4px 2px' } }}
      sx={{ width: 44, '& .MuiOutlinedInput-root': { borderRadius: '6px' }, ...inputSx }}
    />
  );
}

export function CartDrawer({ open, onClose }: CartDrawerProps) {
  const { items, removeItem, updateQty, clear, clearSupplier, selectedRetailerId, selectedRetailerName } =
    useCartStore();
  const user = useAuthStore((s) => s.user);
  const usePtr = user?.tenantType === 'RETAILER' || user?.tenantType === 'REPRESENTATIVE' || user?.tenantType === 'WHOLESALER';

  // Per-supplier placed orders (accumulates as individual suppliers are billed)
  const [partialPlaced, setPartialPlaced] = useState<PlacedEntry[]>([]);
  // Which single supplier is currently being billed (null = none)
  const [placingDistId, setPlacingDistId] = useState<number | null>(null);
  // Is the "Bill All" button running?
  const [globalPlacing, setGlobalPlacing] = useState(false);
  const [error, setError] = useState('');

  // ── Group current cart items by distributor ──────────────────────────────
  const groups = new Map<number, { name: string; city?: string; area?: string; items: typeof items }>();
  for (const item of items) {
    const distId = item.product.distributorTenantId;
    if (!groups.has(distId)) {
      groups.set(distId, { name: item.product.distributorName, city: item.product.distributorCity, area: item.product.distributorArea, items: [] });
    }
    groups.get(distId)!.items.push(item);
  }

  const anyPlacing = placingDistId !== null || globalPlacing;

  const totalAmount = items.reduce((sum, i) => {
    const rate = usePtr
      ? (i.product.ptr ?? i.product.approxRate ?? i.product.mrp ?? 0)
      : (i.product.pts ?? i.product.approxRate ?? i.product.mrp ?? 0);
    const { paid } = computeScheme(i.qty, i.product.schemeHidden ? undefined : i.product.scheme);
    return sum + rate * paid;
  }, 0);

  function groupTotal(groupItems: typeof items) {
    return groupItems.reduce((sum, i) => {
      const rate = usePtr
        ? (i.product.ptr ?? i.product.approxRate ?? i.product.mrp ?? 0)
        : (i.product.pts ?? i.product.approxRate ?? i.product.mrp ?? 0);
      const { paid } = computeScheme(i.qty, i.product.schemeHidden ? undefined : i.product.scheme);
      return sum + rate * paid;
    }, 0);
  }

  // ── Place a single supplier's items ─────────────────────────────────────
  async function handleBillSupplier(distId: number) {
    const group = groups.get(distId);
    if (!group) return;
    const minAmt = group.items[0]?.product.minOrderAmount ?? 0;
    if (minAmt > 0) {
      const total = groupTotal(group.items);
      if (total < minAmt) {
        setError(`Min order for ${group.name} is ₹${minAmt.toFixed(0)} — current ₹${total.toFixed(0)}, need ₹${(minAmt - total).toFixed(0)} more.`);
        return;
      }
    }
    setPlacingDistId(distId);
    setError('');
    try {
      const order = await ordersApi.place({
        distributorTenantId: distId,
        ...(selectedRetailerId ? { retailerTenantId: selectedRetailerId } : {}),
        items: group.items.map((i) => ({ productId: i.product.productId, qty: i.qty })),
      });
      const orderNumber = order.orderNumber || `ORD-${order.id}`;
      setPartialPlaced((prev) => [...prev, { distId, name: group.name, orderNumber }]);
      clearSupplier(distId);
    } catch {
      setError(`Failed to place order for ${group.name}. Please try again.`);
    } finally {
      setPlacingDistId(null);
    }
  }

  // ── Place all remaining suppliers at once ────────────────────────────────
  async function handleBillAll() {
    if (groups.size === 0) return;
    const violations: string[] = [];
    for (const [, group] of groups) {
      const minAmt = group.items[0]?.product.minOrderAmount ?? 0;
      if (minAmt > 0) {
        const total = groupTotal(group.items);
        if (total < minAmt) {
          violations.push(`${group.name}: ₹${total.toFixed(0)} / min ₹${minAmt.toFixed(0)}`);
        }
      }
    }
    if (violations.length > 0) {
      setError(`Minimum order not met — ${violations.join('; ')}`);
      return;
    }
    setGlobalPlacing(true);
    setError('');
    try {
      const results = await Promise.all(
        Array.from(groups.entries()).map(async ([distId, group]) => {
          const order = await ordersApi.place({
            distributorTenantId: distId,
            ...(selectedRetailerId ? { retailerTenantId: selectedRetailerId } : {}),
            items: group.items.map((i) => ({ productId: i.product.productId, qty: i.qty })),
          });
          return { distId, name: group.name, orderNumber: order.orderNumber || `ORD-${order.id}` };
        }),
      );
      setPartialPlaced((prev) => [...prev, ...results]);
      clear();
    } catch {
      setError('One or more orders failed. Please retry.');
    } finally {
      setGlobalPlacing(false);
    }
  }

  function handleClose() {
    setPartialPlaced([]);
    setError('');
    onClose();
  }

  // All done: cart empty and at least one order placed
  const allDone = items.length === 0 && partialPlaced.length > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 500 }, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}
      >
        <ShoppingCartIcon sx={{ color: '#7B61FF', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            Cart
            {items.length > 0 && (
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', ml: 1 }}>
                ({items.length} item{items.length !== 1 ? 's' : ''})
              </Typography>
            )}
          </Typography>
          {selectedRetailerName && (
            <Typography sx={{ fontSize: 11, color: '#7B61FF', fontWeight: 600 }}>
              For: {selectedRetailerName}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </Box>

      {/* ── All done: every supplier billed ── */}
      {allDone && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
          <CheckIcon sx={{ fontSize: 64, color: '#2e7d32' }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#2e7d32' }}>
            All Order{partialPlaced.length > 1 ? 's' : ''} Placed!
          </Typography>
          <Box sx={{ width: '100%' }}>
            {partialPlaced.map((o) => (
              <Box key={o.orderNumber} sx={{ p: 1.5, mb: 1, border: '1px solid #c6f6d5', borderRadius: 1, bgcolor: '#f0fff4' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346' }}>{o.orderNumber}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Sent to {o.name}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
            The distributor(s) will receive your order and can accept or reject it.
          </Typography>
          <Button variant="outlined" onClick={handleClose} sx={{ mt: 1 }}>Close</Button>
        </Box>
      )}

      {/* ── Empty cart ── */}
      {items.length === 0 && partialPlaced.length === 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, color: 'text.secondary', gap: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: '#e0e0e0' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Your cart is empty</Typography>
          <Typography variant="caption" sx={{ textAlign: 'center' }}>
            Search and add products from My Orders, then click "Add to Cart".
          </Typography>
        </Box>
      )}

      {/* ── Active cart ── */}
      {items.length > 0 && (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>

          {/* Already-billed strip (shown at top while cart still has items) */}
          {partialPlaced.length > 0 && (
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Billed ✓
              </Typography>
              {partialPlaced.map((o) => (
                <Box key={o.orderNumber} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CheckIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                  <Typography sx={{ fontSize: 12, color: '#152346', fontWeight: 600, flex: 1 }}>{o.name}</Typography>
                  <Chip label={o.orderNumber} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                </Box>
              ))}
              <Divider sx={{ mt: 1 }} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mx: 2, mt: 1, mb: 0, py: 0.5, fontSize: 12 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* ── Supplier groups ── */}
          {Array.from(groups.entries()).map(([distId, group]) => {
            const gTotal    = groupTotal(group.items);
            const minAmt    = group.items[0]?.product.minOrderAmount ?? 0;
            const belowMin  = minAmt > 0 && gTotal < minAmt;
            const isPlacing = placingDistId === distId;

            return (
              <Box key={distId} sx={{ mb: 0.5 }}>
                {/* Supplier header row */}
                <Box
                  sx={{
                    px: 2, py: 1,
                    bgcolor: '#f0f4ff',
                    borderBottom: '1px solid', borderTop: '1px solid', borderColor: '#d4dcff',
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346' }} noWrap>
                      {group.name}
                    </Typography>
                    {(group.area || group.city) && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {[group.area, group.city].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title={`Bill only ${group.name}'s items now`}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={anyPlacing}
                        onClick={() => handleBillSupplier(distId)}
                        startIcon={
                          isPlacing
                            ? <CircularProgress size={11} color="inherit" />
                            : <ReceiptIcon sx={{ fontSize: 13 }} />
                        }
                        sx={{
                          fontSize: 10, fontWeight: 700, py: 0.4, px: 1.25, whiteSpace: 'nowrap',
                          bgcolor: '#1a237e', '&:hover': { bgcolor: '#283593' },
                          '&.Mui-disabled': { opacity: 0.5 },
                        }}
                      >
                        {isPlacing ? 'Billing…' : 'Bill This'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {/* Items */}
                {group.items.map((item) => {
                  const rate = usePtr
                    ? (item.product.ptr ?? item.product.approxRate ?? item.product.mrp ?? 0)
                    : (item.product.pts ?? item.product.approxRate ?? item.product.mrp ?? 0);
                  const showScheme = item.product.scheme && !item.product.schemeHidden;
                  const { paid, free } = computeScheme(item.qty, showScheme ? item.product.scheme : undefined);
                  const lineTotal = (rate * paid).toFixed(2);
                  const paidDisplay = paid % 1 === 0 ? Math.floor(paid) : paid;
                  const scheme = showScheme ? item.product.scheme : undefined;
                  const commitQty = (rawQty: number) => {
                    if (!Number.isFinite(rawQty)) return;
                    const capped = capQtyToAvailable(Math.max(1, Math.floor(rawQty)), scheme, item.product.availableQty, item.product.stockHidden);
                    const { paid: newPaid } = computeScheme(capped, scheme, item.product.availableQty ?? undefined);
                    updateQty(item.key, newPaid);
                  };

                  return (
                    <Box
                      key={item.key}
                      sx={{
                        px: 2, py: 1.25,
                        borderBottom: '1px solid', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.product.name}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                          {[item.product.brand, item.product.packSize].filter(Boolean).join(' · ')}
                        </Typography>
                        {showScheme && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={`Scheme ${item.product.scheme}`}
                              size="small"
                              sx={{ height: 18, fontSize: 10, bgcolor: '#ede9ff', color: '#5b3dc7', '& .MuiChip-label': { px: '6px' } }}
                            />
                            {free > 0 && (
                              <Chip
                                label={`+${free % 1 === 0 ? Math.floor(free) : free} FREE`}
                                size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#d1fae5', color: '#065f46', '& .MuiChip-label': { px: '6px' } }}
                              />
                            )}
                          </Box>
                        )}
                        <Typography sx={{ fontSize: 11, color: '#152346', fontWeight: 600, mt: 0.25 }}>
                          ₹{rate.toFixed(2)} × {paidDisplay} = <strong>₹{lineTotal}</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                        {!item.product.stockHidden && (item.product.availableQty ?? 0) > 0 ? (
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            stock: {item.product.availableQty}
                          </Typography>
                        ) : item.product.stockHidden ? (
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            available
                          </Typography>
                        ) : <Box />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const rawQty = paid % 1 === 0 ? Math.floor(paid) : paid - 0.5;
                              commitQty(rawQty - 1);
                            }}
                            sx={{ width: 26, height: 26, bgcolor: '#f3f4f6', borderRadius: '6px', '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <Typography sx={{ fontSize: 17, lineHeight: 1, fontWeight: 700, color: '#374151' }}>−</Typography>
                          </IconButton>
                          <CartQtyField
                            storeQty={paidDisplay}
                            onCommit={commitQty}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const rawQty = paid % 1 === 0 ? Math.floor(paid) : paid - 0.5;
                              commitQty(rawQty + 1);
                            }}
                            sx={{ width: 26, height: 26, bgcolor: '#f3f4f6', borderRadius: '6px', '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <Typography sx={{ fontSize: 17, lineHeight: 1, fontWeight: 700, color: '#374151' }}>+</Typography>
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeItem(item.key)}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

                {/* Group subtotal + min order warning */}
                <Box sx={{ px: 2, py: 0.75, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Subtotal:{' '}
                      <Typography component="span" sx={{ fontWeight: 700, color: '#152346', fontSize: 12 }}>
                        ₹{gTotal.toFixed(2)}
                      </Typography>
                    </Typography>
                  </Box>
                  {belowMin && (
                    <Alert severity="warning" icon={false} sx={{ mt: 0.5, py: 0.25, px: 1, fontSize: 11, '& .MuiAlert-message': { p: 0 } }}>
                      Min order ₹{minAmt.toFixed(0)} — add ₹{(minAmt - gTotal).toFixed(0)} more
                    </Alert>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Footer ── */}
      {items.length > 0 && (
        <Box sx={{ p: 2, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {groups.size} supplier{groups.size !== 1 ? 's' : ''} · {items.length} item{items.length !== 1 ? 's' : ''}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', display: 'block' }}>
                {groups.size > 1 ? 'REMAINING TOTAL' : 'TOTAL'}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#152346' }}>
                ₹{totalAmount.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={clear}
              disabled={anyPlacing}
              sx={{ fontSize: 11, flex: 1 }}
            >
              Clear Cart
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleBillAll}
              disabled={anyPlacing}
              startIcon={
                globalPlacing
                  ? <CircularProgress size={12} color="inherit" />
                  : <ShoppingCartIcon sx={{ fontSize: 15 }} />
              }
              sx={{
                fontSize: 11, flex: 2, fontWeight: 700,
                bgcolor: '#7B61FF', '&:hover': { bgcolor: '#6a50ee' },
              }}
            >
              {globalPlacing
                ? 'Billing…'
                : groups.size > 1
                  ? `Bill All ${groups.size} Suppliers`
                  : 'Bill Order'}
            </Button>
          </Box>

          {groups.size > 1 && (
            <Typography sx={{ fontSize: 10, color: 'text.secondary', textAlign: 'center', mt: 0.75 }}>
              Use "Bill This" on each supplier to bill them separately
            </Typography>
          )}
        </Box>
      )}
    </Drawer>
  );
}
